// This file is auto-generated by @hey-api/openapi-ts
import type { CancelablePromise } from './core/CancelablePromise'
import { OpenAPI } from './core/OpenAPI'
import { request as __request } from './core/request'
import type {
  GetUserData,
  GetUserResponse,
  CheckEmailData,
  CheckEmailResponse2,
  UpdateUserData,
  UpdateUserResponse,
  GetOnboardingResponse,
  SubmitOnboardingData,
  SubmitOnboardingResponse,
  GetSubscriptionData,
  GetSubscriptionResponse,
  CreateStripeSubscriptionData,
  CreateStripeSubscriptionResponse,
  CancelStripeSubscriptionData,
  CancelStripeSubscriptionResponse,
  ContinueStripeSubscriptionData,
  ContinueStripeSubscriptionResponse,
  GetProductsResponse,
  GetPlansData,
  GetPlansResponse,
  StartCallData,
  StartCallResponse,
  UpdateCallData,
  UpdateCallResponse,
  GetBioPageData,
  GetBioPageResponse,
  GetHomepageResponse,
  GetFeedbackOptionsResponse,
  CreateConversationData,
  CreateConversationResponse,
  GetConversationData,
  GetConversationResponse,
  UpdateConversationData,
  UpdateConversationResponse,
  AddMessageToConversationData,
  AddMessageToConversationResponse,
  FinishConversationData,
  FinishConversationResponse,
  GetContentCategoriesData,
  GetContentCategoriesResponse,
  GetContentsData,
  GetContentsResponse,
  GetContentDetailsData,
  GetContentDetailsResponse,
  UpdateUserContentHistoryData,
  UpdateUserContentHistoryResponse,
  GetUserContentHistoryData,
  GetUserContentHistoryResponse,
  AppleSubscriptionConfirmData,
  AppleSubscriptionConfirmResponse2,
  GetRoleplayHomepageData,
  GetRoleplayHomepageResponse,
  GetRoleplayDetailData,
  GetRoleplayDetailResponse,
  CreateRoleplayData,
  CreateRoleplayResponse,
  GetRoleplayFeedbackData,
  GetRoleplayFeedbackResponse,
  GetHomepageForYouResponse,
  GetTextMessageHistoryData,
  GetTextMessageHistoryResponse,
  SendBotMessageData,
  SendBotMessageResponse,
  GetPrePaywallCategoryData,
  GetPrePaywallCategoryResponse,
  GetPrePaywallHomepageResponse,
  GetPrePaywallBioData,
  GetPrePaywallBioResponse,
} from './types.gen'

export class UsersService {
  /**
   * Get user details
   * Get the details of a user by UUID. This endpoint will return the user's details, including their accessible products.
   * A user can only access their own details.
   *
   * @param data The data for the request.
   * @param data.uuid The UUID of the user, or "me" to fetch the current user.
   *
   * @returns User OK
   * @throws ApiError
   */
  public static getUser(data: GetUserData): CancelablePromise<GetUserResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/users/{uuid}',
      path: {
        uuid: data.uuid,
      },
      errors: {
        401: `Unauthorized
The user is not authenticated.
`,
        404: 'Not Found',
      },
    })
  }

  /**
   * Check if an email is available
   * Check if an email is available for registration. This endpoint will return a list of identities that match the email.
   *
   * @param data The data for the request.
   * @param data.email The email to check.
   *
   * @returns CheckEmailResponse OK
   * @throws ApiError
   */
  public static checkEmail(
    data: CheckEmailData,
  ): CancelablePromise<CheckEmailResponse2> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/check-email',
      query: {
        email: data.email,
      },
      errors: {
        400: 'Bad Request',
        404: 'Not Found',
      },
    })
  }
}

export class UserService {
  /**
   * Update user
   * Updates an existing user.
   * @param data The data for the request.
   * @param data.uuid The UUID of the User or "me" to update current user.
   *
   * @param data.requestBody
   * @returns unknown OK
   * @throws ApiError
   */
  public static updateUser(
    data: UpdateUserData,
  ): CancelablePromise<UpdateUserResponse> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/users/{uuid}',
      path: {
        uuid: data.uuid,
      },
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        400: 'The request body was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found. Either the resource does not exist or the user does not have authorization to access the resource.',
      },
    })
  }
}

export class OnboardingService {
  /**
   * Get persona onboarding information
   * Retrieves the onboarding information including instructors, skills, and careers.
   * @returns PersonaOnboardingResponse OK Response containing onboarding information
   * @throws ApiError
   */
  public static getOnboarding(): CancelablePromise<GetOnboardingResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/onboarding',
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found or the user does not have authorization to access it.',
      },
    })
  }

  /**
   * Submit persona onboarding information
   * Submits the onboarding information including instructors, skills, and careers.
   * @param data The data for the request.
   * @param data.requestBody
   * @returns unknown The onboarding information was submitted successfully.
   * @throws ApiError
   */
  public static submitOnboarding(
    data: SubmitOnboardingData,
  ): CancelablePromise<SubmitOnboardingResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/onboarding',
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        500: 'Error response containing error information',
      },
    })
  }
}

export class SubscriptionsService {
  /**
   * Returns a subscription
   * Return the details of a subscription by ID.
   *
   * @param data The data for the request.
   * @param data.id The ID of the subscription or "me" to fetch the current user's subscription.
   *
   * @returns CommerceGatewaySubscription OK
   * @throws ApiError
   */
  public static getSubscription(
    data: GetSubscriptionData,
  ): CancelablePromise<GetSubscriptionResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/commerce/subscriptions/{id}',
      path: {
        id: data.id,
      },
      errors: {
        400: 'Bad Request',
        404: 'Not Found',
      },
    })
  }
}

export class StripeService {
  /**
   * Create a new Stripe subscription
   * This endpoint creates a new subscription in Stripe.
   * The user must be authenticated to use this endpoint, or if the user is not authenticated,
   * they must provide an email address in the body of the request.
   * The user can not be entitled to more than one product at a time, so check the user's entitlements
   * before creating a new subscription. See the getUser operation for more information.
   *
   * @param data The data for the request.
   * @param data.requestBody
   * @returns StripeSubscriptionResponse OK
   * @throws ApiError
   */
  public static createStripeSubscription(
    data: CreateStripeSubscriptionData,
  ): CancelablePromise<CreateStripeSubscriptionResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/commerce/stripe/subscriptions',
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        400: 'Bad Request',
      },
    })
  }

  /**
   * Cancel a Stripe subscription
   * This endpoint deletes a Stripe subscription.
   * The user must be authenticated to use this endpoint, and can only cancel their own subscription.
   * This operation will not delete the subscription from Stripe, but will update the subscription such that
   * it will not renew at the end of the current billing period.
   *
   * @param data The data for the request.
   * @param data.subscriptionId The ID of the Stripe subscription to cancel.
   * @returns StripeSubscriptionResponse OK
   * @throws ApiError
   */
  public static cancelStripeSubscription(
    data: CancelStripeSubscriptionData,
  ): CancelablePromise<CancelStripeSubscriptionResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/commerce/stripe/subscriptions/{subscription_id}/cancel',
      path: {
        subscription_id: data.subscriptionId,
      },
      errors: {
        400: 'Bad Request',
        404: 'Not Found',
      },
    })
  }

  /**
   * Continue a Stripe subscription
   * This endpoint continues a Stripe subscription that was previously set to expire.
   * The user must be authenticated to use this endpoint, and can only continue their own subscription.
   * This operation will update the subscription such that it will renew at the end of the current billing period.
   *
   * @param data The data for the request.
   * @param data.subscriptionId The ID of the Stripe subscription to continue.
   * @returns StripeSubscriptionResponse OK
   * @throws ApiError
   */
  public static continueStripeSubscription(
    data: ContinueStripeSubscriptionData,
  ): CancelablePromise<ContinueStripeSubscriptionResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/commerce/stripe/subscriptions/{subscription_id}/continue',
      path: {
        subscription_id: data.subscriptionId,
      },
      errors: {
        400: 'Bad Request',
        404: 'Not Found',
      },
    })
  }
}

export class ProductsService {
  /**
   * Returns available products to purchase
   * Returns available products to be purchased. Based on user-agent header API will return apple or stripe products.
   *
   * @returns ProductsResponse OK
   * @throws ApiError
   */
  public static getProducts(): CancelablePromise<GetProductsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/commerce/products',
      errors: {
        400: 'Bad Request',
        404: 'Not Found',
      },
    })
  }
}

export class PlansService {
  /**
   * Returns available purchase plans
   * Returns available plans available to be purchased.
   *
   * @param data The data for the request.
   * @param data.gateway The commerce gateway to retrieve plans for. If a gateway is provided, only plans available for that gateway will be returned.
   *
   * @param data.currency The currency to filter pricing_plans by. Case insensitive, will be converted to lowercase on the server.
   *
   * @param data.country The country to filter pricing_plans by. Case insensitive, will be converted to lowercase on the server.
   *
   * @param data.includeTrial Whether or not to include plans that have a trial period. Defaults to false.
   *
   * @returns Plan OK
   * @throws ApiError
   */
  public static getPlans(
    data: GetPlansData = {},
  ): CancelablePromise<GetPlansResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/commerce/plans',
      query: {
        gateway: data.gateway,
        currency: data.currency,
        country: data.country,
        include_trial: data.includeTrial,
      },
      errors: {
        400: 'Bad Request',
        404: 'Not Found',
      },
    })
  }
}

export class PersonaService {
  /**
   * To start a new call with a bot or roleplay scenario
   * Initiates a new session with a bot. Can be used for voice calls, text chats, roleplay sessions. For roleplay scenarios, include the roleplay_scenario object in the request and 'roleplay' value for kind property.
   * @param data The data for the request.
   * @param data.requestBody
   * @returns CallResponse OK Response containing call details
   * @throws ApiError
   */
  public static startCall(
    data: StartCallData = {},
  ): CancelablePromise<StartCallResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/calls',
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        400: 'The request body was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found. Either the resource does not exist or the user does not have authorization to access the resource.',
        503: 'OK Response containing call details',
      },
    })
  }

  /**
   * Update an existing call
   * Updates an existing call.
   * @param data The data for the request.
   * @param data.callUuid UUID of the call
   * @param data.requestBody
   * @returns unknown OK
   * @throws ApiError
   */
  public static updateCall(
    data: UpdateCallData,
  ): CancelablePromise<UpdateCallResponse> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/calls/{call_uuid}',
      path: {
        call_uuid: data.callUuid,
      },
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        400: 'The request body was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found. Either the resource does not exist or the user does not have authorization to access the resource.',
      },
    })
  }

  /**
   * Get bio page data for a instructor
   * Retrieves bio page data for a instructor.
   * @param data The data for the request.
   * @param data.slug Slug of the instructor
   * @returns BioPageResponse OK Response containing bio page data
   * @throws ApiError
   */
  public static getBioPage(
    data: GetBioPageData,
  ): CancelablePromise<GetBioPageResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/bio-page/{slug}',
      path: {
        slug: data.slug,
      },
      errors: {
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found. Either the resource does not exist or the user does not have authorization to access the resource.',
      },
    })
  }

  /**
   * Get homepage data
   * Retrieves homepage data.
   * @returns HomepageResponse OK Response containing homepage data
   * @throws ApiError
   */
  public static getHomepage(): CancelablePromise<GetHomepageResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/homepage',
      errors: {
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found. Either the resource does not exist or the user does not have authorization to access the resource.',
      },
    })
  }

  /**
   * Get feedback options
   * Retrieves feedback options.
   * @returns FeedbackResponse OK Response containing feedback options
   * @throws ApiError
   */
  public static getFeedbackOptions(): CancelablePromise<GetFeedbackOptionsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/persona/feedback',
      errors: {
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found. Either the resource does not exist or the user does not have authorization to access the resource.',
      },
    })
  }
}

export class ConversationsService {
  /**
   * Create new conversation
   * @param data The data for the request.
   * @param data.requestBody
   * @returns Conversation OK
   * @throws ApiError
   */
  public static createConversation(
    data: CreateConversationData = {},
  ): CancelablePromise<CreateConversationResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/conversations',
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        401: 'unauthorized',
        422: 'unprocessable entity',
      },
    })
  }

  /**
   * Get a conversation
   * @param data The data for the request.
   * @param data.uuid UUID of the conversation
   * @returns Conversation OK
   * @throws ApiError
   */
  public static getConversation(
    data: GetConversationData,
  ): CancelablePromise<GetConversationResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/conversations/{uuid}',
      path: {
        uuid: data.uuid,
      },
      errors: {
        401: 'unauthorized',
        404: 'conversation not found',
        422: 'unprocessable entity',
      },
    })
  }

  /**
   * Update an existing conversation details
   * @param data The data for the request.
   * @param data.uuid UUID of the conversation
   * @param data.requestBody
   * @returns Conversation OK
   * @throws ApiError
   */
  public static updateConversation(
    data: UpdateConversationData,
  ): CancelablePromise<UpdateConversationResponse> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/conversations/{uuid}',
      path: {
        uuid: data.uuid,
      },
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        401: 'unauthorized',
        404: 'conversation not found',
        422: 'unprocessable entity',
      },
    })
  }

  /**
   * Create a message in a conversation
   * @param data The data for the request.
   * @param data.uuid UUID of the conversation
   * @param data.requestBody
   * @returns Message OK
   * @throws ApiError
   */
  public static addMessageToConversation(
    data: AddMessageToConversationData,
  ): CancelablePromise<AddMessageToConversationResponse> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/conversations/{uuid}/message',
      path: {
        uuid: data.uuid,
      },
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        401: 'unauthorized',
        404: 'conversation not found',
        422: 'unprocessable entity',
      },
    })
  }

  /**
   * Finish a conversation
   * @param data The data for the request.
   * @param data.uuid UUID of the conversation
   * @param data.requestBody
   * @returns unknown OK
   * @throws ApiError
   */
  public static finishConversation(
    data: FinishConversationData,
  ): CancelablePromise<FinishConversationResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/conversations/{uuid}/finish',
      path: {
        uuid: data.uuid,
      },
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        401: 'unauthorized',
        404: 'conversation not found',
      },
    })
  }
}

export class CategoriesService {
  /**
   * Get a list of content categories
   * Retrieves a list of all content categories.
   * @param data The data for the request.
   * @param data.instructorSlug Slug of the instructor
   * @returns ContentCategoriesResponse OK Response containing content categories
   * @throws ApiError
   */
  public static getContentCategories(
    data: GetContentCategoriesData,
  ): CancelablePromise<GetContentCategoriesResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/personas/{instructor_slug}/categories',
      path: {
        instructor_slug: data.instructorSlug,
      },
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found or the user does not have authorization to access it.',
      },
    })
  }
}

export class ContentsService {
  /**
   * Get list of content items
   * Get list of content items, filtered by category UUID or content UUIDs
   * @param data The data for the request.
   * @param data.instructorSlug instructor_slug
   * @param data.parentUuid UUID of the category or the parent to filter contents
   * @param data.uuids Comma separated UUIDs of content items to retrieve
   * @param data.page Current page of data being requested
   * @param data.pageSize Number of items to return per page
   * @returns ContentsResponse OK Response containing a list of content items
   * @throws ApiError
   */
  public static getContents(
    data: GetContentsData,
  ): CancelablePromise<GetContentsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/personas/{instructor_slug}/contents',
      path: {
        instructor_slug: data.instructorSlug,
      },
      query: {
        parent_uuid: data.parentUuid,
        uuids: data.uuids,
        page: data.page,
        page_size: data.pageSize,
      },
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'One or more of the requested items could not be found or the user does not have authorization to access them.',
      },
    })
  }

  /**
   * Get details of a content item
   * Retrieves details of a specific content item.
   * @param data The data for the request.
   * @param data.instructorSlug Instructor slug
   * @param data.contentUuid UUID of content
   * @returns ContentDetail OK Response containing full content details
   * @throws ApiError
   */
  public static getContentDetails(
    data: GetContentDetailsData,
  ): CancelablePromise<GetContentDetailsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/personas/{instructor_slug}/contents/{content_uuid}',
      path: {
        instructor_slug: data.instructorSlug,
        content_uuid: data.contentUuid,
      },
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found or the user does not have authorization to access it.',
      },
    })
  }

  /**
   * Update user content history
   * Updates the user's content history for a specified instructor
   * @param data The data for the request.
   * @param data.instructorSlug Instructor slug
   * @param data.requestBody
   * @returns void OK
   * @throws ApiError
   */
  public static updateUserContentHistory(
    data: UpdateUserContentHistoryData,
  ): CancelablePromise<UpdateUserContentHistoryResponse> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/personas/{instructor_slug}/contents/user_history',
      path: {
        instructor_slug: data.instructorSlug,
      },
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        400: 'The request body was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found or the user does not have authorization to access it.',
      },
    })
  }

  /**
   * Get user contents history
   * Retrieves the user's content history for a given instructor
   * @param data The data for the request.
   * @param data.instructorSlug Instructor slug
   * @param data.page Current page of data being requested
   * @param data.pageSize Number of items to return per page
   * @returns ContentHistoryResponse OK Response containing a list of user history items ordered by most recent
   * @throws ApiError
   */
  public static getUserContentHistory(
    data: GetUserContentHistoryData,
  ): CancelablePromise<GetUserContentHistoryResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/personas/{instructor_slug}/contents/user_history',
      path: {
        instructor_slug: data.instructorSlug,
      },
      query: {
        page: data.page,
        page_size: data.pageSize,
      },
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found or the user does not have authorization to access it.',
      },
    })
  }
}

export class AppleSubscriptionsConfirmService {
  /**
   * Confim Apple Subscription
   * This endpoint will create a subscription based on a JWT
   *
   * @param data The data for the request.
   * @param data.requestBody Confirmation attributes
   * @returns AppleSubscriptionConfirmResponse OK
   * @throws ApiError
   */
  public static appleSubscriptionConfirm(
    data: AppleSubscriptionConfirmData,
  ): CancelablePromise<AppleSubscriptionConfirmResponse2> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/commerce/apple/subscriptions/confirm',
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request
JWS could not be decoded.
`,
        401: `Unauthorized
The user is not authenticated.
`,
        404: 'Not Found',
      },
    })
  }
}

export class RoleplayService {
  /**
   * Get roleplay homepage information
   * Retrieves the roleplay homepage information, including categories, user status, and UI text.
   * @param data The data for the request.
   * @param data.categoryUuidOrSlug UUID or slug of the category to select (defaults to 0th category if missing)
   * @param data.showYourPath Whether to show the user's path
   * @returns RoleplayHomepageResponse OK Response containing roleplay homepage information
   * @throws ApiError
   */
  public static getRoleplayHomepage(
    data: GetRoleplayHomepageData = {},
  ): CancelablePromise<GetRoleplayHomepageResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/roleplay-homepage',
      query: {
        category_uuid_or_slug: data.categoryUuidOrSlug,
        show_your_path: data.showYourPath,
      },
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found or the user does not have authorization to access it.',
      },
    })
  }

  /**
   * Get roleplay scenario detail
   * Retrieves details for a roleplay scenario. If scenario_uuid is omitted, a custom roleplay is returned with all available ai_roles and ai_personalities.
   * @param data The data for the request.
   * @param data.scenarioUuidOrSlug Scenario UUID or slug. If omitted or is 'custom', the response will include selections with all ai_roles and all ai_personalities.
   * @param data.userRolePlayUuid User role play UUID. If provided, the response will include the user role play details for a custom roleplay.
   * @returns RoleplayDetailResponse OK
   * @throws ApiError
   */
  public static getRoleplayDetail(
    data: GetRoleplayDetailData,
  ): CancelablePromise<GetRoleplayDetailResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/roleplay-detail/{scenario_uuid_or_slug}',
      path: {
        scenario_uuid_or_slug: data.scenarioUuidOrSlug,
      },
      query: {
        user_role_play_uuid: data.userRolePlayUuid,
      },
      errors: {
        400: 'Bad Request',
        401: 'Unauthorized',
        404: 'Not Found',
      },
    })
  }

  /**
   * Create a new roleplay scenario
   * Creates a new roleplay scenario with the specified parameters.
   * @param data The data for the request.
   * @param data.requestBody
   * @returns RoleplayCreateResponse OK Response containing the created roleplay scenario details
   * @throws ApiError
   */
  public static createRoleplay(
    data: CreateRoleplayData = {},
  ): CancelablePromise<CreateRoleplayResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/roleplay',
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        402: 'OK Response containing the created roleplay scenario details',
        404: 'The resource could not be found or the user does not have authorization to access it.',
      },
    })
  }

  /**
   * Get feedback for a roleplay scenario
   * Retrieves feedback for a roleplay scenario.
   * @param data The data for the request.
   * @param data.conversationUuid Conversation UUID.
   * @param data.async
   * @returns RoleplayFeedbackResponse OK
   * @throws ApiError
   */
  public static getRoleplayFeedback(
    data: GetRoleplayFeedbackData,
  ): CancelablePromise<GetRoleplayFeedbackResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/roleplay-feedback/{conversation_uuid}',
      path: {
        conversation_uuid: data.conversationUuid,
      },
      query: {
        async: data.async,
      },
      errors: {
        400: 'Bad Request',
        401: 'Unauthorized',
        404: 'Not Found',
        422: 'Unprocessable Entity',
      },
    })
  }
}

export class HomepageForYouService {
  /**
   * Get homepage-for-you information
   * Retrieves the homepage-for-you information
   * @returns HomepageForYouResponse OK Response containing homepage information
   * @throws ApiError
   */
  public static getHomepageForYou(): CancelablePromise<GetHomepageForYouResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/homepage-for-you',
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found or the user does not have authorization to access it.',
      },
    })
  }
}

export class MessagesService {
  /**
   * Get TextMessage History
   * @param data The data for the request.
   * @param data.conversationUuid
   * @param data.cursor
   * @param data.limit
   * @returns unknown The request was successful
   * @throws ApiError
   */
  public static getTextMessageHistory(
    data: GetTextMessageHistoryData,
  ): CancelablePromise<GetTextMessageHistoryResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/chat',
      query: {
        conversation_uuid: data.conversationUuid,
        cursor: data.cursor,
        limit: data.limit,
      },
      errors: {
        400: 'The request body was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found. Either the resource does not exist or the user does not have authorization to access the resource.',
        503: 'The server is down for maintenance or unavailable',
      },
    })
  }

  /**
   * Send a text message to a bot
   * Send a text message to a bot
   * @param data The data for the request.
   * @param data.requestBody
   * @returns MessageResponse The request was successful
   * @throws ApiError
   */
  public static sendBotMessage(
    data: SendBotMessageData = {},
  ): CancelablePromise<SendBotMessageResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/chat',
      body: data.requestBody,
      mediaType: 'application/json',
      errors: {
        400: 'The request body was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found. Either the resource does not exist or the user does not have authorization to access the resource.',
        413: 'Message content exceeds the maximum allowed length.',
        503: 'The server is down for maintenance or unavailable',
      },
    })
  }
}

export class PrePaywallService {
  /**
   * Get pre-paywall category information
   * Retrieves the pre-paywall category information, including AMA topics, testimonials, instructors, and scenarios.
   * @param data The data for the request.
   * @param data.slug Slug of the category to retrieve
   * @returns PrePaywallCategoryResponse OK Response containing pre-paywall category information
   * @throws ApiError
   */
  public static getPrePaywallCategory(
    data: GetPrePaywallCategoryData,
  ): CancelablePromise<GetPrePaywallCategoryResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/pre-paywall-categories/{slug}',
      path: {
        slug: data.slug,
      },
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found or the user does not have authorization to access it.',
      },
    })
  }

  /**
   * Get pre-paywall homepage information
   * Retrieves the pre-paywall homepage information, including AMA topics, testimonials, categories, instructors, and scenarios.
   * @returns PrePaywallHomepageResponse OK Response containing pre-paywall homepage information
   * @throws ApiError
   */
  public static getPrePaywallHomepage(): CancelablePromise<GetPrePaywallHomepageResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/pre-paywall-homepage',
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found or the user does not have authorization to access it.',
      },
    })
  }

  /**
   * Get pre-paywall bio page information
   * Retrieves the pre-paywall bio page information, including testimonials, categories, instructors, and scenarios.
   * @param data The data for the request.
   * @param data.slug Slug of the instructor to retrieve bio for
   * @returns PrePaywallBioResponse OK Response containing pre-paywall bio page information
   * @throws ApiError
   */
  public static getPrePaywallBio(
    data: GetPrePaywallBioData,
  ): CancelablePromise<GetPrePaywallBioResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/pre-paywall-bio/{slug}',
      path: {
        slug: data.slug,
      },
      errors: {
        400: 'The request was invalid.',
        401: 'The authentication credentials required to access this resource are missing or invalid.',
        404: 'The resource could not be found or the user does not have authorization to access it.',
      },
    })
  }
}
